// NOTE: PDF polyfills are now lazy-loaded via @/utils/pdfPolyfills.ts
// This reduces initial bundle size by ~640KB (gzipped)

// Initialize Sentry FIRST before any other imports that might throw
import { initSentry } from './monitoring/sentry';
initSentry();

// Validate URL security EARLY - fails fast if insecure URLs in production
import { validateProductionUrls } from './lib/env/validateUrls';
validateProductionUrls();

// Check for environment mismatches (prod domain with dev config, etc.)
import { checkEnvMismatch } from './lib/env/envMismatchGuard';
checkEnvMismatch();

import { createRoot } from 'react-dom/client';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initPerformanceMonitoring } from './lib/monitoring/performance';
import { runStartupDiagnostics } from './lib/startup/diagnostics';
import App from './App.tsx';
import './index.css';
import './app/i18n/i18n';
import { logger } from '@/lib/logger';

// Async initialization wrapper to avoid top-level await
(async () => {
  try {
    // Load dev-only network guard as early as possible
    if (import.meta.env.DEV) {
      await import('./app/dev/networkGuard');
    }

    // Run startup diagnostics and auto-healing
    await runStartupDiagnostics();

    // Initialize performance monitoring
    initPerformanceMonitoring();

    // Render app
    createRoot(document.getElementById('root')!).render(
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    logger.error('Failed to initialize app:', error);
    // Show error to user
    document.getElementById('root')!.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui;">
        <div style="text-align: center; padding: 2rem;">
          <h1 style="color: #dc2626; margin-bottom: 1rem;">Failed to load application</h1>
          <p style="color: #6b7280;">Please refresh the page or contact support if the problem persists.</p>
        </div>
      </div>
    `;
  }
})().catch((error) => {
  logger.error('Unhandled initialization error:', error);
});
